/**
 * DatePicker - Clara Vale V3 styled date picker.
 *
 * Hybrid free-text input + calendar popover. The text input accepts the
 * locale-formatted date (e.g. dd/MM/yyyy in en-GB, MM/dd/yyyy in en-US).
 * The calendar header uses double-chevron year nav and a clickable
 * year/month label that reveals a compact year-grid view for fast
 * multi-decade jumps - inspired by Linear, Vercel, and macOS Calendar.
 * All colours use Clara Vale tokens.
 */

import { useState, useRef, useEffect, useLayoutEffect, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { DayPicker, type Matcher } from 'react-day-picker'
import {
  format,
  parse,
  isValid,
  addMonths,
  addYears,
  setYear,
  setMonth,
} from 'date-fns'
import {
  CalendarDays,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from 'lucide-react'
import { cn } from '@/shared/lib/utils'
import {
  formatShortDate,
  getDateFnsLocale,
  useFormattingContext,
} from '@/shared/lib/formatting'

const CALENDAR_WIDTH = 280
const CALENDAR_HEIGHT_FALLBACK = 320
const YEAR_GRID_SIZE = 12 // 3 cols × 4 rows

/**
 * Derive a date-fns format pattern from the locale's short-date parts so
 * the input and the parser agree on order + separator (dd/MM/yyyy vs
 * MM/dd/yyyy vs yyyy-MM-dd etc.).
 */
function getLocaleDateFormat(locale: string): string {
  try {
    const parts = new Intl.DateTimeFormat(locale, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).formatToParts(new Date(2000, 0, 1))
    return parts
      .map((p) => {
        switch (p.type) {
          case 'day':
            return 'dd'
          case 'month':
            return 'MM'
          case 'year':
            return 'yyyy'
          default:
            return p.value
        }
      })
      .join('')
  } catch {
    return 'dd/MM/yyyy'
  }
}

interface DatePickerProps {
  value: string // ISO date string YYYY-MM-DD
  onChange: (value: string) => void
  placeholder?: string
  required?: boolean
  className?: string
  /** Disable dates before today - for future-only fields like scheduling. */
  disablePast?: boolean
  /**
   * Disable dates after today - for past-only fields like incident /
   * purchase / loss dates. The calendar disables future days and a typed
   * future date is rejected.
   */
  disableFuture?: boolean
  /** Paint the error border. Matches PhoneInput / CurrencyInput. */
  hasError?: boolean
  'data-testid'?: string
  /**
   * Optional explicit BCP 47 locale tag. When omitted, the picker uses the
   * org-wide locale from `FormattingProvider` (which is wired to the
   * organisation's default country in admin settings).
   */
  locale?: string
}

type PickerView = 'days' | 'months' | 'years'

export function DatePicker({
  value,
  onChange,
  placeholder,
  className,
  hasError = false,
  'data-testid': testId,
  locale: localeProp,
  disablePast = false,
  disableFuture = false,
}: DatePickerProps) {
  const { locale: orgLocale } = useFormattingContext()
  // "Today" is computed fresh on every render (never memoized at mount) so a
  // session left open across midnight - common for back-office/CS users -
  // still validates against the real current day. react-day-picker does not
  // require the `disabled` prop to be referentially stable.
  const disabledMatchers: Matcher[] = []
  if (disablePast) {
    const start = new Date()
    start.setHours(0, 0, 0, 0)
    disabledMatchers.push({ before: start })
  }
  if (disableFuture) {
    const end = new Date()
    end.setHours(23, 59, 59, 999)
    disabledMatchers.push({ after: end })
  }
  const locale = localeProp || orgLocale
  const dateFnsLocale = getDateFnsLocale(locale)
  const formatPattern = useMemo(() => getLocaleDateFormat(locale), [locale])

  const [open, setOpen] = useState(false)
  const [view, setView] = useState<PickerView>('days')
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const popoverRef = useRef<HTMLDivElement>(null)
  const [popoverStyle, setPopoverStyle] = useState<{
    top: number
    left: number
    maxHeight: number
  } | null>(null)

  const selectedDate = value ? parse(value, 'yyyy-MM-dd', new Date()) : undefined
  const hasValidSelection = selectedDate && isValid(selectedDate)
  const displayValue = hasValidSelection ? formatShortDate(selectedDate!, locale) : ''

  const [inputValue, setInputValue] = useState<string>(displayValue)
  const [isFocused, setIsFocused] = useState(false)
  const [lastSyncedDisplay, setLastSyncedDisplay] = useState(displayValue)
  if (!isFocused && displayValue !== lastSyncedDisplay) {
    setLastSyncedDisplay(displayValue)
    setInputValue(displayValue)
  }

  const [calendarMonth, setCalendarMonth] = useState<Date>(
    hasValidSelection ? (selectedDate as Date) : new Date(),
  )
  const [lastSyncedValue, setLastSyncedValue] = useState(value)
  if (value !== lastSyncedValue) {
    setLastSyncedValue(value)
    if (hasValidSelection) setCalendarMonth(selectedDate as Date)
  }

  useLayoutEffect(() => {
    if (!open) return
    function reposition() {
      const trigger = containerRef.current
      if (!trigger) return
      const rect = trigger.getBoundingClientRect()
      const viewportHeight = window.innerHeight
      const viewportWidth = window.innerWidth
      const measuredHeight =
        popoverRef.current?.offsetHeight || CALENDAR_HEIGHT_FALLBACK
      const spaceBelow = viewportHeight - rect.bottom - 8
      const spaceAbove = rect.top - 8
      const placeAbove =
        spaceBelow < measuredHeight + 4 && spaceAbove > spaceBelow
      // Cap the popover to the space on the chosen side so it can never
      // overflow the viewport or float detached from the field - when
      // the trigger sits low in a tall form the calendar stays flush
      // above the input and scrolls internally instead of clamping to
      // the top of the page and blanketing unrelated fields.
      const available = (placeAbove ? spaceAbove : spaceBelow) - 4
      const maxHeight = Math.max(220, available)
      const height = Math.min(measuredHeight, maxHeight)
      const top = placeAbove
        ? Math.max(8, rect.top - height - 4)
        : rect.bottom + 4
      const left = Math.min(
        Math.max(8, rect.left),
        viewportWidth - CALENDAR_WIDTH - 8,
      )
      setPopoverStyle({ top, left, maxHeight })
    }
    reposition()
    const raf = requestAnimationFrame(reposition)
    window.addEventListener('scroll', reposition, true)
    window.addEventListener('resize', reposition)
    return () => {
      cancelAnimationFrame(raf)
      window.removeEventListener('scroll', reposition, true)
      window.removeEventListener('resize', reposition)
    }
  }, [open, view])

  useEffect(() => {
    if (!open) return
    function onClickOutside(e: MouseEvent) {
      const target = e.target as Node
      if (containerRef.current?.contains(target)) return
      if (popoverRef.current?.contains(target)) return
      setOpen(false)
    }
    document.addEventListener('mousedown', onClickOutside)
    return () => document.removeEventListener('mousedown', onClickOutside)
  }, [open])

  useEffect(() => {
    if (!open) return
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [open])

  const handleSelect = (date: Date | undefined) => {
    if (date) {
      onChange(format(date, 'yyyy-MM-dd'))
      setInputValue(formatShortDate(date, locale))
    }
    setOpen(false)
  }

  const commitInput = (raw: string) => {
    const trimmed = raw.trim()
    if (!trimmed) {
      if (value) onChange('')
      setInputValue('')
      return
    }
    const parsed = parse(trimmed, formatPattern, new Date())
    if (isValid(parsed)) {
      // Reject a typed date the calendar would disable. Boundaries are
      // computed fresh so they stay correct even across midnight, and both
      // constraints are enforced so the typed path matches the calendar.
      const start = new Date()
      start.setHours(0, 0, 0, 0)
      const end = new Date()
      end.setHours(23, 59, 59, 999)
      if ((disablePast && parsed < start) || (disableFuture && parsed > end)) {
        setInputValue(displayValue)
        return
      }
      const iso = format(parsed, 'yyyy-MM-dd')
      if (iso !== value) onChange(iso)
      setInputValue(formatShortDate(parsed, locale))
      setCalendarMonth(parsed)
      return
    }
    setInputValue(displayValue)
  }

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      commitInput(inputValue)
      inputRef.current?.blur()
    }
  }

  // Header - double-chevron year nav, clickable label for month/year views.
  const navButtonCls =
    'inline-flex h-6 w-6 shrink-0 cursor-pointer items-center justify-center rounded-[4px] border-none bg-transparent text-[var(--ink-3)] transition-colors hover:bg-[var(--bg-subtle)] hover:text-[var(--ink)]'

  const renderDayHeader = () => (
    <div className="flex items-center justify-between px-1 pb-2">
      <div className="flex items-center gap-0.5">
        <button
          type="button"
          aria-label="Previous year"
          onClick={() => setCalendarMonth(addYears(calendarMonth, -1))}
          className={navButtonCls}
        >
          <ChevronsLeft size={14} />
        </button>
        <button
          type="button"
          aria-label="Previous month"
          onClick={() => setCalendarMonth(addMonths(calendarMonth, -1))}
          className={navButtonCls}
        >
          <ChevronLeft size={14} />
        </button>
      </div>
      <div className="flex items-center gap-1">
        <button
          type="button"
          aria-label="Change month"
          onClick={() => setView('months')}
          className="inline-flex cursor-pointer items-center gap-0.5 rounded-[4px] border-[0.5px] border-[var(--rule)] bg-transparent px-2 py-1 text-sm text-[var(--ink)] transition-colors hover:border-[var(--green)] hover:bg-[var(--bg-subtle)]"
        >
          {format(calendarMonth, 'MMMM', { locale: dateFnsLocale })}
          <ChevronDown size={12} className="text-[var(--ink-3)]" />
        </button>
        <button
          type="button"
          aria-label="Change year"
          onClick={() => setView('years')}
          className="inline-flex cursor-pointer items-center gap-0.5 rounded-[4px] border-[0.5px] border-[var(--rule)] bg-transparent px-2 py-1 text-sm text-[var(--ink)] transition-colors hover:border-[var(--green)] hover:bg-[var(--bg-subtle)]"
        >
          {format(calendarMonth, 'yyyy', { locale: dateFnsLocale })}
          <ChevronDown size={12} className="text-[var(--ink-3)]" />
        </button>
      </div>
      <div className="flex items-center gap-0.5">
        <button
          type="button"
          aria-label="Next month"
          onClick={() => setCalendarMonth(addMonths(calendarMonth, 1))}
          className={navButtonCls}
        >
          <ChevronRight size={14} />
        </button>
        <button
          type="button"
          aria-label="Next year"
          onClick={() => setCalendarMonth(addYears(calendarMonth, 1))}
          className={navButtonCls}
        >
          <ChevronsRight size={14} />
        </button>
      </div>
    </div>
  )

  const currentYear = calendarMonth.getFullYear()
  const yearGridStart = Math.floor(currentYear / 12) * 12
  const years = Array.from({ length: YEAR_GRID_SIZE }, (_, i) => yearGridStart + i)

  const renderYearGrid = () => (
    <div>
      <div className="flex items-center justify-between px-1 pb-2">
        <button
          type="button"
          aria-label="Previous years"
          onClick={() => setCalendarMonth(addYears(calendarMonth, -12))}
          className={navButtonCls}
        >
          <ChevronLeft size={14} />
        </button>
        <span className="text-sm text-[var(--ink)]">
          {yearGridStart} - {yearGridStart + 11}
        </span>
        <button
          type="button"
          aria-label="Next years"
          onClick={() => setCalendarMonth(addYears(calendarMonth, 12))}
          className={navButtonCls}
        >
          <ChevronRight size={14} />
        </button>
      </div>
      <div className="grid grid-cols-3 gap-1">
        {years.map((y) => {
          const isCurrent = y === currentYear
          return (
            <button
              key={y}
              type="button"
              onClick={() => {
                setCalendarMonth(setYear(calendarMonth, y))
                setView('months')
              }}
              className={cn(
                'cursor-pointer rounded-[4px] border-none py-2 text-sm transition-colors',
                isCurrent
                  ? 'bg-[var(--green)] text-white hover:bg-[var(--green)]'
                  : 'bg-transparent text-[var(--ink)] hover:bg-[var(--bg-subtle)]',
              )}
            >
              {y}
            </button>
          )
        })}
      </div>
    </div>
  )

  const renderMonthGrid = () => {
    const currentMonth = calendarMonth.getMonth()
    const months = Array.from({ length: 12 }, (_, i) =>
      format(new Date(currentYear, i, 1), 'MMM', { locale: dateFnsLocale }),
    )
    return (
      <div>
        <div className="flex items-center justify-between px-1 pb-2">
          <button
            type="button"
            aria-label="Previous year"
            onClick={() => setCalendarMonth(addYears(calendarMonth, -1))}
            className={navButtonCls}
          >
            <ChevronLeft size={14} />
          </button>
          <button
            type="button"
            onClick={() => setView('years')}
            className="cursor-pointer rounded-[4px] bg-transparent px-2 py-1 text-sm text-[var(--ink)] transition-colors hover:bg-[var(--bg-subtle)]"
          >
            {currentYear}
          </button>
          <button
            type="button"
            aria-label="Next year"
            onClick={() => setCalendarMonth(addYears(calendarMonth, 1))}
            className={navButtonCls}
          >
            <ChevronRight size={14} />
          </button>
        </div>
        <div className="grid grid-cols-3 gap-1">
          {months.map((label, i) => {
            const isCurrent = i === currentMonth
            return (
              <button
                key={i}
                type="button"
                onClick={() => {
                  setCalendarMonth(setMonth(calendarMonth, i))
                  setView('days')
                }}
                className={cn(
                  'cursor-pointer rounded-[4px] border-none py-2 text-sm capitalize transition-colors',
                  isCurrent
                    ? 'bg-[var(--green)] text-white hover:bg-[var(--green)]'
                    : 'bg-transparent text-[var(--ink)] hover:bg-[var(--bg-subtle)]',
                )}
              >
                {label}
              </button>
            )
          })}
        </div>
      </div>
    )
  }

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      <div
        className={cn(
          'flex h-9 w-full items-center rounded-[4px] border border-input bg-[var(--bg-light)] pl-3 pr-1 transition-all duration-150',
          (open || isFocused) && 'border-[var(--green)]',
          // Error outranks focus: it must stay visible while the user fixes it.
          hasError && 'border-destructive',
        )}
      >
        <input
          ref={inputRef}
          type="text"
          data-testid={testId}
          value={inputValue}
          placeholder={placeholder || formatPattern.toLowerCase()}
          onChange={(e) => setInputValue(e.target.value)}
          onFocus={() => {
            setIsFocused(true)
            setView('days')
            setOpen(true)
          }}
          onBlur={() => {
            setIsFocused(false)
            commitInput(inputValue)
          }}
          onKeyDown={handleInputKeyDown}
          aria-invalid={hasError || undefined}
          inputMode="numeric"
          autoComplete="off"
          className="h-full w-full min-w-0 flex-1 bg-transparent text-base text-[var(--ink)] outline-none placeholder:text-[var(--ink-3)]"
        />
        <button
          type="button"
          aria-label="Open calendar"
          onClick={() => {
            setOpen((v) => {
              if (!v) setView('days')
              return !v
            })
          }}
          className={cn(
            'ml-1 inline-flex h-7 w-7 shrink-0 cursor-pointer items-center justify-center rounded-[4px] border-none bg-transparent text-[var(--ink-3)] transition-colors hover:bg-[var(--bg-subtle)]',
            open && 'text-[var(--green)]',
          )}
        >
          <CalendarDays size={14} />
        </button>
      </div>
      {open && popoverStyle && typeof document !== 'undefined'
        ? createPortal(
            <div
              ref={popoverRef}
              style={{
                position: 'fixed',
                top: popoverStyle.top,
                left: popoverStyle.left,
                width: CALENDAR_WIDTH,
                maxHeight: popoverStyle.maxHeight,
                overflowY: 'auto',
              }}
              className="z-[60] rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] p-3 shadow-[0_4px_12px_var(--overlay-light)]"
            >
              {view === 'years' ? (
                renderYearGrid()
              ) : view === 'months' ? (
                renderMonthGrid()
              ) : (
                <>
                  {renderDayHeader()}
                  <DayPicker
                    mode="single"
                    disabled={
                      disabledMatchers.length ? disabledMatchers : undefined
                    }
                    selected={selectedDate}
                    onSelect={handleSelect}
                    month={calendarMonth}
                    onMonthChange={setCalendarMonth}
                    locale={dateFnsLocale}
                    showOutsideDays
                    hideNavigation
                    classNames={{
                      root: 'clara-calendar',
                      months: 'flex flex-col',
                      month: 'flex flex-col gap-2',
                      month_caption: 'hidden',
                      caption_label: 'hidden',
                      nav: 'hidden',
                      weekdays: 'grid grid-cols-7 gap-0',
                      weekday:
                        'w-8 text-center text-xs font-normal uppercase tracking-[0.08em] text-[var(--ink-3)]',
                      week: 'grid grid-cols-7 gap-0',
                      weeks: 'flex flex-col gap-0',
                      day: 'flex h-8 w-8 items-center justify-center rounded-[4px] text-sm text-[var(--ink)] transition-colors cursor-pointer hover:bg-[var(--bg-subtle)]',
                      day_button:
                        'h-full w-full rounded-[4px] cursor-pointer bg-transparent border-none text-inherit',
                      selected:
                        '!bg-[var(--green)] !text-white hover:!bg-[var(--green)]',
                      today: 'font-normal text-[var(--green)]',
                      outside: 'text-[var(--ink-3)] opacity-40',
                      disabled: 'opacity-30 cursor-not-allowed',
                    }}
                  />
                </>
              )}
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
